import { usePostHog as usePostHogBase } from 'posthog-js/react';

// Custom hook to use PostHog analytics in the legal workspace
export const usePostHog = () => {
  const posthog = usePostHogBase();

  // Track workspace-specific events with error handling
  const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
    try {
      if (posthog && typeof posthog.capture === 'function') {
        posthog.capture(eventName, {
          ...properties,
          context: 'legal_workspace',
          app: 'hector_legal',
          platform: 'web'
        });
      } else {
      }
    } catch (error) {
    }
  };

  // Track document-related events
  const trackDocument = (action: 'upload' | 'upload_complete' | 'view' | 'download' | 'delete' | 'share', metadata?: Record<string, unknown>) => {
    trackEvent(`document_${action}`, {
      ...metadata,
      feature: 'document_management'
    });
  };

  // Track case management events
  const trackCase = (action: 'create' | 'view' | 'edit' | 'delete' | 'assign', caseId?: string, metadata?: Record<string, unknown>) => {
    trackEvent(`case_${action}`, {
      case_id: caseId,
      ...metadata,
      feature: 'case_management'
    });
  };

  // Track client management events
  const trackClient = (action: 'create' | 'view' | 'edit' | 'delete', clientId?: string, metadata?: Record<string, unknown>) => {
    trackEvent(`client_${action}`, {
      client_id: clientId,
      ...metadata,
      feature: 'client_management'
    });
  };

  // Track synthesis events
  const trackSynthesis = (action: 'start' | 'complete' | 'error' | 'retry', documentId?: string, metadata?: Record<string, unknown>) => {
    trackEvent(`synthesis_${action}`, {
      document_id: documentId,
      ...metadata,
      feature: 'ai_synthesis'
    });
  };

  // Track authentication events
  const trackAuth = (action: 'login' | 'logout' | 'signup' | 'password_reset', userEmail?: string) => {
    trackEvent(`auth_${action}`, {
      email: userEmail,
      timestamp: new Date().toISOString(),
      feature: 'authentication'
    });
  };

  // Track navigation/page views
  const trackPageView = (pageName: string, metadata?: Record<string, unknown>) => {
    try {
      if (posthog && typeof posthog.capture === 'function') {
        posthog.capture('$pageview', {
          page_name: pageName,
          context: 'legal_workspace',
          ...metadata
        });
      } else {
      }
    } catch (error) {
    }
  };

  // Identify user with enhanced legal workspace context
  const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
    try {
      if (posthog && typeof posthog.identify === 'function') {
        posthog.identify(userId, {
          ...properties,
          app: 'hector_legal',
          identified_at: new Date().toISOString(),
          platform: 'web',
          user_type: 'legal_professional',
        });

        // Enregistrer les super properties — propagées automatiquement
        // à TOUS les events PostHog suivants, y compris error_captured
        if (properties?.cabinet_id) {
          posthog.register({
            cabinet_id: properties.cabinet_id,
            app: 'hector_legal',
            platform: 'web',
          });
        }
      }
    } catch {
      // Silencieux
    }
  };

  // Track cabinet/workspace events
  const trackCabinet = (action: 'create' | 'join' | 'leave' | 'invite', cabinetId?: string, metadata?: Record<string, unknown>) => {
    trackEvent(`cabinet_${action}`, {
      cabinet_id: cabinetId,
      ...metadata,
      feature: 'workspace_management'
    });
  };

  // Track subscription/billing events
  const trackSubscription = (action: 'upgrade' | 'downgrade' | 'cancel' | 'renew', planType?: string) => {
    trackEvent(`subscription_${action}`, {
      plan_type: planType,
      timestamp: new Date().toISOString(),
      feature: 'billing'
    });
  };

  // Track legal template usage
  const trackTemplate = (action: 'view' | 'use' | 'create' | 'edit', templateId?: string, metadata?: Record<string, unknown>) => {
    trackEvent(`template_${action}`, {
      template_id: templateId,
      ...metadata,
      feature: 'legal_templates'
    });
  };

  // Track search and filtering
  // Ne pas envoyer le texte de recherche — peut contenir des données client (RGPD)
  const trackSearch = (_query: string, results: number, context: string) => {
    trackEvent('search_performed', {
      query_length: _query.length,
      results_count: results,
      search_context: context,
      feature: 'search'
    });
  };

  // Track feature usage
  const trackFeature = (featureName: string, context?: Record<string, unknown>) => {
    trackEvent('feature_used', {
      feature_name: featureName,
      ...context,
      timestamp: new Date().toISOString()
    });
  };

  return {
    posthog,
    trackEvent,
    trackDocument,
    trackCase,
    trackClient,
    trackSynthesis,
    trackAuth,
    trackPageView,
    identifyUser,
    trackCabinet,
    trackSubscription,
    trackTemplate,
    trackSearch,
    trackFeature,
  };
};
